<template>
  <button
    type="button"
    class="relative flex h-9 w-9 items-center justify-center"
    :disabled="disabled"
    :class="{ 'opacity-40': disabled }"
    @click.stop="emit('toggle')"
  >
    <template v-if="variant === 'button'">
      <div
        class="flex size-9 items-center justify-center rounded-xl transition-colors duration-150"
        :class="
          isActive
            ? 'bg-background-negative-invisible'
            : 'bg-background-neutral-onblock'
        "
      >
        <Transition name="icon-switch" mode="out-in">
          <FavoriteActiveIcon v-if="isActive" key="active" class="size-6" />
          <FavoriteIcon v-else key="default" class="size-6 text-gray-400" />
        </Transition>
      </div>
    </template>
    <template v-else>
      <Transition name="icon-switch" mode="out-in">
        <FavoriteActiveIcon v-if="isActive" key="active" class="size-6" />
        <FavoriteIcon
          v-else
          key="default"
          class="text-text-neutral-primary size-6"
        />
      </Transition>
    </template>

    <div
      v-for="(particle, index) in particles"
      :key="`particle-${index}-${particle.id}`"
      class="pointer-events-none absolute"
      :style="{
        left: PARTICLES_ORIGIN_LEFT,
        top: PARTICLES_ORIGIN_TOP,
        transform: PARTICLES_TRANSFORM,
      }"
    >
      <div
        class="heart-particle absolute"
        :style="{
          '--dx': particle.dx + 'px',
          '--dy': particle.dy + 'px',
          '--rotation': particle.rotation + 'deg',
          '--delay': particle.delay + 'ms',
          '--scale': particle.scale,
          animationDuration: particle.duration + 'ms',
        }"
      >
        <FavoriteActiveIcon class="size-3" />
      </div>
    </div>
  </button>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import {
    useFavoriteAnimation,
    PARTICLES_ORIGIN_LEFT,
    PARTICLES_ORIGIN_TOP,
    PARTICLES_TRANSFORM,
  } from 'freedom-shared';

  import FavoriteIcon from '@/assets/images/favorite-icon.svg?component';
  import FavoriteActiveIcon from '@/assets/images/favorite-active-icon.svg?component';

  const props = withDefaults(
    defineProps<{
      isActive: boolean;
      variant?: 'button' | 'icon';
      disabled?: boolean;
    }>(),
    {
      variant: 'button',
      disabled: false,
    },
  );

  const emit = defineEmits<{ toggle: [] }>();

  const { particles } = useFavoriteAnimation(computed(() => props.isActive));
</script>

<style scoped>
  .icon-switch-enter-active,
  .icon-switch-leave-active {
    transition:
      opacity 150ms ease,
      transform 150ms ease;
  }

  .icon-switch-enter-from,
  .icon-switch-leave-to {
    opacity: 0;
    transform: scale(0.6);
  }

  .heart-particle {
    animation: heartBurst linear forwards;
    opacity: 1;
  }

  @keyframes heartBurst {
    0% {
      transform: translate(-50%, -50%) scale(0) rotate(0deg);
      opacity: 1;
    }
    10% {
      transform: translate(-50%, -50%) scale(var(--scale))
        rotate(var(--rotation));
      opacity: 1;
    }
    100% {
      transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy)))
        scale(0) rotate(calc(var(--rotation) + 180deg));
      opacity: 0;
    }
  }
</style>
